/**
 * Detection / Break-Glass - Phase 4 wiring.
 *
 * Live data sources:
 *   GET  /v1/detection/break-glass
 *   POST /v1/detection/break-glass/activate
 *   POST /v1/detection/break-glass/:id/approve
 *   POST /v1/detection/break-glass/:id/file-summary
 *
 * Banking Two-Person Rule:
 * - Requester drafts justification + permissions + duration.
 * - A second distinct DPO/IT-Admin approves via `/approve`.
 * - When the session ends, requester files a follow-up summary via
 *   `/file-summary` (audit-chain attached).
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { KeyRound, AlertTriangle, Clock, User, CheckCircle2, Loader2, FileText } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Separator } from '@/components/ui/primitives';
import {
  listBreakGlassSessions,
  activateBreakGlass,
  approveBreakGlass,
  fileBreakGlassSummary,
  type BreakGlassSession,
} from '@/lib/api';

function bucket(s: BreakGlassSession): 'active' | 'pending' | 'closed' {
  // Server-side `ddr_break_glass_sessions` has no literal status
  // column - derive it from (approvedByUserId, expiresAt, revokedAt,
  // followUpFiledAt). A row is:
  //   • pending  → not approved yet
  //   • closed   → revoked OR expired OR follow-up filed
  //   • active   → approved, not yet expired/revoked, no follow-up
  if (!s.approvedByUserId) return 'pending';
  const now = Date.now();
  const expired = s.expiresAt && new Date(s.expiresAt).getTime() < now;
  if (s.revokedAt || s.followUpFiledAt || expired) return 'closed';
  return 'active';
}

function relTime(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(Math.abs(ms) / 1000);
  const sign = ms < 0 ? 'in ' : '';
  if (s < 60) return `${sign}${s}s${ms >= 0 ? ' ago' : ''}`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${sign}${m}m${ms >= 0 ? ' ago' : ''}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${sign}${h}h${ms >= 0 ? ' ago' : ''}`;
  return `${sign}${Math.floor(h / 24)}d${ms >= 0 ? ' ago' : ''}`;
}

function minutesUntil(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 60_000));
}

export function DetectionBreakGlass() {
  const [sessions, setSessions] = useState<BreakGlassSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [justification, setJustification] = useState('');
  const [duration, setDuration] = useState(60);
  const [permissions, setPermissions] = useState('discovery:read,activity:read');
  const [submitting, setSubmitting] = useState(false);

  const [pendingId, setPendingId] = useState<string | null>(null);

  const [summaryFor, setSummaryFor] = useState<BreakGlassSession | null>(null);
  const [summaryText, setSummaryText] = useState('');

  const refresh = (signal?: AbortSignal) =>
    listBreakGlassSessions()
      .then((r) => !signal?.aborted && setSessions(r.sessions))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const active  = useMemo(() => sessions.filter((s) => bucket(s) === 'active'), [sessions]);
  const pending = useMemo(() => sessions.filter((s) => bucket(s) === 'pending'), [sessions]);
  const closed  = useMemo(() => sessions.filter((s) => bucket(s) === 'closed'), [sessions]);

  async function onSubmitRequest() {
    if (justification.length < 20) return;
    setSubmitting(true);
    try {
      await activateBreakGlass({
        justification,
        requestedPermissions: permissions.split(',').map((p) => p.trim()).filter(Boolean),
        durationMinutes: duration,
      });
      setRequestModalOpen(false);
      setJustification('');
      setDuration(60);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'request failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function onApprove(s: BreakGlassSession) {
    setPendingId(s.id);
    try {
      await approveBreakGlass(s.id, { approverNote: 'Approved by second person via UI' });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'approve failed');
    } finally {
      setPendingId(null);
    }
  }

  async function onSubmitSummary() {
    if (!summaryFor || !summaryText.trim()) return;
    setPendingId(summaryFor.id);
    try {
      await fileBreakGlassSummary(summaryFor.id, {
        summary: summaryText,
        actionsTaken: summaryText.split('\n').map((l) => l.trim()).filter(Boolean),
      });
      setSummaryFor(null);
      setSummaryText('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'file failed');
    } finally {
      setPendingId(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Break-Glass"
        subtitle="Banking Two-Person Rule. Every break-glass session requires justification, expiry window, and a second distinct approver. Every action hash-chained."
        badges={[
          { label: 'Banking 2P', variant: 'warning' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [
                  { label: `${active.length} active`, variant: active.length > 0 ? ('critical' as const) : ('success' as const) },
                  { label: `${pending.length} pending`, variant: pending.length > 0 ? ('warning' as const) : ('glass' as const) },
                ]),
        ]}
        actions={
          <Button size="sm" onClick={() => setRequestModalOpen(true)}>
            <KeyRound className="h-4 w-4" /> Request break-glass
          </Button>
        }
      />

      {loading && (
        <LoadingState caption="Loading break-glass sessions…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Some break-glass data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Active + Pending column */}
          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-3">
              <CardTitle>Pending approvals + active sessions</CardTitle>
              <CardDescription>
                {pending.length} awaiting 2P approval · {active.length} currently elevated
              </CardDescription>
            </CardHeader>
            {pending.length === 0 && active.length === 0 && (
              <div className="text-xs text-muted-foreground py-6 text-center">No active or pending sessions.</div>
            )}
            {pending.map((s) => (
              <div key={s.id} className="glass-lite border border-warning/40 p-4 rounded-md space-y-3 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-warning" />
                    <code className="text-xs font-mono">{s.userId}</code>
                  </div>
                  <Badge variant="warning" className="text-[9px]">PENDING 2P</Badge>
                </div>
                <p className="text-xs text-muted-foreground italic border-l-2 border-warning pl-3">"{s.justification}"</p>
                <div className="text-[10px] text-muted-foreground">Requested {relTime(s.createdAt)}</div>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => onApprove(s)}
                  disabled={pendingId === s.id}
                  className="w-full"
                >
                  {pendingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                  Approve (2P)
                </Button>
              </div>
            ))}
            {active.map((s) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-lite glow-high p-4 rounded-md space-y-3 mb-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-warning animate-pulse" />
                    <code className="text-xs font-mono">{s.userId}</code>
                  </div>
                  <Badge variant="warning" className="text-[9px]">ACTIVE</Badge>
                </div>
                <p className="text-xs text-muted-foreground italic border-l-2 border-warning pl-3">"{s.justification}"</p>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Requested</div>
                    <div className="tabular">{relTime(s.createdAt)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expires in</div>
                    <div className="tabular text-warning">{minutesUntil(s.expiresAt)}m</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Approver (2P)</div>
                    <div className="font-mono truncate">{s.approvedByUserId ?? '-'}</div>
                  </div>
                </div>
                <Button variant="glass" size="sm" className="w-full" onClick={() => setSummaryFor(s)}>
                  <FileText className="h-3 w-3" /> File summary + close
                </Button>
              </motion.div>
            ))}
          </GlassCard>

          {/* Closed column */}
          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-3">
              <CardTitle>Recent sessions</CardTitle>
              <CardDescription>Closed / expired · audit-chain attached</CardDescription>
            </CardHeader>
            {closed.length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">No closed sessions in history yet.</div>
            ) : (
              <div className="space-y-2">
                {closed.slice(0, 8).map((s, i) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="glass-lite p-3 rounded-md"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <code className="text-xs font-mono">{s.userId}</code>
                      <Badge variant="success" className="text-[9px]">
                        <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                        {s.followUpSummary ? 'Summary filed' : 'Closed'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-2 italic">"{s.justification}"</p>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1.5">
                      <Clock className="h-3 w-3" /> {relTime(s.createdAt)}
                      {s.approvedByUserId && (
                        <><User className="h-3 w-3" /> 2P: <code className="font-mono">{s.approvedByUserId.slice(0, 18)}…</code></>
                      )}
                    </div>
                    {s.followUpSummary && (
                      <p className="text-[10px] text-muted-foreground/80 mt-1 line-clamp-2">{s.followUpSummary}</p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      )}

      {/* Request modal */}
      <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="h-5 w-5" />
              Request break-glass session
            </DialogTitle>
            <DialogDescription>
              Banking Two-Person Rule applies. Your DPO or IT Admin must approve. Every action is hash-chained.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Justification (20+ chars)</label>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                className="w-full glass-lite rounded-md p-3 text-sm outline-none resize-none"
                placeholder="Explain why standard role permissions are insufficient. This justification is permanently in the audit chain."
              />
              <div className="text-[10px] text-muted-foreground mt-1 tabular">{justification.length} / 20+ chars · {justification.length >= 20 ? '✓' : '✗ too short'}</div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Requested permissions (comma-separated)</label>
              <input
                value={permissions}
                onChange={(e) => setPermissions(e.target.value)}
                className="w-full glass-lite rounded-md p-2 text-sm font-mono"
                placeholder="discovery:read,activity:read"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Expiry (15-240 min)</label>
              <input
                type="range" min={15} max={240} step={15}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-xs tabular text-warning mt-1">{duration} min · auto-revoke at expiry</div>
            </div>
            <Separator />
            <div className="text-[11px] text-muted-foreground">
              On submit, this request is sent to your designated 2P approvers. The approver MUST be a different person than you. No self-approval. Every action during the session is recorded and reviewed.
            </div>
            <div className="flex gap-2">
              <Button variant="glass" className="flex-1" onClick={() => setRequestModalOpen(false)} disabled={submitting}>Cancel</Button>
              <Button variant="warning" className="flex-1" disabled={justification.length < 20 || submitting} onClick={onSubmitRequest}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Request approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* File-summary modal */}
      <Dialog open={!!summaryFor} onOpenChange={(o) => !o && setSummaryFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>File post-session summary</DialogTitle>
            <DialogDescription>
              Capture what you did during the elevated session. Stored in the audit chain for review by Legal / Compliance.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={summaryText}
            onChange={(e) => setSummaryText(e.target.value)}
            rows={6}
            className="w-full glass-lite rounded-md p-3 text-sm outline-none resize-none mt-2"
            placeholder="One action per line:
- Pulled forensic timeline for BLK-…
- Compared statement-hash chain across 3 actors
- Filed final report at /opt/incidents/INC-2026-…"
          />
          <div className="flex gap-2 mt-3">
            <Button variant="glass" className="flex-1" onClick={() => setSummaryFor(null)}>Cancel</Button>
            <Button className="flex-1" disabled={!summaryText.trim() || pendingId === summaryFor?.id} onClick={onSubmitSummary}>
              {pendingId === summaryFor?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              File summary
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
